<template>
  <div ref="root" class="nbell">
    <button
      class="nbell__btn"
      type="button"
      :aria-label="$t('notifications.title')"
      :aria-expanded="store.open ? 'true' : 'false'"
      @click="store.togglePanel()"
    >
      <span class="nbell__icon" :style="{ '--nbell-icon': cssIconUrl(bellIcon) }" aria-hidden="true" />
      <!-- Бейджа нет, когда счётчик неизвестен: пустое место честнее нуля. -->
      <span v-if="badgeLabel" class="nbell__badge">{{ badgeLabel }}</span>
    </button>

    <div v-if="store.open" class="nbell__panel">
      <p class="nbell__title">{{ $t('notifications.title') }}</p>

      <p v-if="store.loading && store.items.length === 0" class="nbell__note">
        {{ $t('notifications.loading') }}
      </p>

      <div v-else-if="store.failed" class="nbell__fail">
        <p class="nbell__note">{{ $t('notifications.error') }}</p>
        <button class="nbell__retry" type="button" @click="store.load()">
          {{ $t('notifications.retry') }}
        </button>
      </div>

      <p v-else-if="store.isEmpty" class="nbell__note">{{ $t('notifications.empty') }}</p>

      <ul v-else class="nbell__list">
        <li
          v-for="item in store.items"
          :key="item.id"
          class="nbell__item"
          :class="{ 'nbell__item--new': item.isNew }"
        >
          <button class="nbell__row" type="button" @click="store.openItem(item.id)">
            <span class="nbell__head">
              <span class="nbell__name">
                <span
                  v-if="item.isNew"
                  class="nbell__dot"
                  :aria-label="$t('notifications.unread')"
                  role="img"
                />
                {{ item.title }}
              </span>
              <span v-if="item.date" class="nbell__date">{{ item.date }}</span>
            </span>
            <span class="nbell__preview">{{ item.preview }}</span>
          </button>
        </li>
      </ul>
    </div>

    <BaseModal
      :open="store.activeItem !== null"
      :title="store.activeItem?.title"
      size="md"
      @close="store.closeItem()"
    >
      <article v-if="store.activeItem" class="nbell-note">
        <p v-if="store.activeItem.date" class="nbell-note__date">{{ store.activeItem.date }}</p>
        <h2 class="nbell-note__title">{{ store.activeItem.title }}</h2>
        <!-- eslint-disable-next-line vue/no-v-html -- HTML обеззаражен в api/notificationsApi.ts до попадания в состояние -->
        <div class="nbell-note__body" v-html="store.activeItem.html" />
      </article>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
/**
 * Колокольчик уведомлений в шапке кабинета.
 *
 * Живёт только у вошедшего: шапка не рисует его гостю (см. AppHeader.vue),
 * поэтому здесь нет своей проверки доступа — монтирование уже означает «свой».
 *
 * Иконка — маска, а не `<img>`: файл белый, и картинкой он остался бы белым
 * навсегда. Маска красится `background-color`, поэтому подхватывает и наведение.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import bellIcon from '@/assets/images/icons/sections/notifications.svg'
import BaseModal from '@/shared/components/base/BaseModal.vue'
// Кавычки вокруг URL ставит `cssIconUrl`: без них собранный data-URI ломает
// маску, и на месте колокольчика остаётся сплошной квадрат.
import { cssIconUrl } from '@/shared/utils/assets'
import { useNotificationsStore } from '../store/notificationsStore'

/** Больше сотни непрочитанных бейдж не показывает — иначе он разъезжается. */
const BADGE_MAX = 99

const store = useNotificationsStore()
const root = ref<HTMLElement | null>(null)

const badgeLabel = computed<string>(() => {
  const count = store.badgeCount
  if (count === null) return ''

  return count > BADGE_MAX ? `${BADGE_MAX}+` : String(count)
})

// Пока открыто окно уведомления, панель под ним живёт: клик внутри окна
// (оно телепортировано в body) и Escape закрывают окно, а не список.
function onDocumentClick(event: MouseEvent): void {
  if (!store.open || store.activeId) return
  const target = event.target as Node | null
  if (root.value && target && !root.value.contains(target)) {
    store.closePanel()
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && !store.activeId) store.closePanel()
}

onMounted(() => {
  store.startPolling()
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  store.stopPolling()
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped lang="scss">
// Подача igra: воздух и типографика, без карточных рамок. Панель отделена от
// шапки тенью и фоном, а строки — только отступами и разделителем.
.nbell {
  position: relative;
  display: flex;
  align-items: center;

  &__btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    width: 32px;
    height: 32px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--ds-text-secondary);

    &:hover {
      color: var(--ds-accent);
    }
  }

  &__icon {
    width: 22px;
    height: 22px;
    background-color: currentColor;
    mask: var(--nbell-icon) no-repeat center / contain;
    -webkit-mask: var(--nbell-icon) no-repeat center / contain;
  }

  &__badge {
    position: absolute;
    top: -2px;
    right: -4px;
    min-width: 16px;
    padding: 0 4px;
    border-radius: 999px;
    background: var(--ds-accent);
    color: var(--ds-text-on-accent);
    font-size: 10px;
    font-weight: var(--ds-weight-semibold);
    line-height: 16px;
    text-align: center;
  }

  &__panel {
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    z-index: 60;
    width: min(340px, calc(100vw - 32px));
    max-height: 70vh;
    overflow-y: auto;
    padding: 18px 18px 8px;
    background: var(--ds-surface);
    border-radius: var(--ds-radius-card, 18px);
    box-shadow: var(--ds-elevation-3);
  }

  &__title {
    margin: 0 0 14px;
    font-size: 15px;
    font-weight: var(--ds-weight-semibold);
    color: var(--ds-text);
  }

  &__note {
    margin: 0;
    padding: 6px 0 16px;
    font-size: 13px;
    color: var(--ds-text-muted, var(--ds-text-secondary));
  }

  &__retry {
    margin-bottom: 14px;
    padding: 6px 14px;
    background: transparent;
    border: 1px solid var(--ds-border);
    border-radius: var(--ds-radius-control);
    color: var(--ds-text);
    font-size: 13px;
    cursor: pointer;
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__item + &__item {
    border-top: 1px solid var(--ds-border);
  }

  &__row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    padding: 12px 0;
    background: transparent;
    border: none;
    text-align: left;
    cursor: pointer;
    color: var(--ds-text-secondary);
  }

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
  }

  &__name {
    font-size: 14px;
    font-weight: var(--ds-weight-semibold);
    color: var(--ds-text-secondary);
  }

  // Непрочитанное — единственное, что выделено цветом в списке.
  &__item--new &__name {
    color: var(--ds-text);
  }

  &__date {
    flex: none;
    font-size: 11px;
    color: var(--ds-text-muted, var(--ds-text-secondary));
  }

  &__preview {
    font-size: 13px;
    line-height: 1.45;
  }

  &__dot {
    display: inline-block;
    flex: none;
    width: 8px;
    height: 8px;
    margin-right: 8px;
    border-radius: 50%;
    background: var(--ds-accent);
    vertical-align: middle;
  }

  // Прочитанное приглушено целиком, чтобы новое читалось с первого взгляда.
  &__item:not(&__item--new) &__row {
    opacity: 0.6;
  }

  &__row:hover &__name {
    color: var(--ds-accent);
  }
}
</style>

<style lang="scss">
// Окно телепортировано в body: scoped-стили до него не доезжают.
.nbell-note {
  color: var(--ds-text);
  overflow-wrap: anywhere;

  &__date {
    margin: 0 0 6px;
    font-size: 12px;
    color: var(--ds-text-muted, var(--ds-text-secondary));
  }

  &__title {
    margin: 0 0 16px;
    font-size: 20px;
    font-weight: var(--ds-weight-semibold);
    line-height: 1.3;
  }

  &__body {
    font-size: 15px;
    line-height: 1.6;
    color: var(--ds-text-secondary);

    p {
      margin: 0 0 12px;
    }

    a {
      color: var(--ds-accent);
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }
}
</style>
